export type DocType = 'proposal' | 'contingency' | 'change_order' | 'estimate' | 'supplement';
export type ProposalStatus = 'draft' | 'sent' | 'viewed' | 'signed' | 'declined';

export type BlockType =
  | 'cover'
  | 'text'
  | 'image'
  | 'line_items_table'
  | 'good_better_best'
  | 'totals_summary'
  | 'signature'
  | 'terms'
  | 'marketing_gallery'
  | 'dynamic_image'
  | 'smart_field'
  | 'video_embed'
  | 'button'
  | 'package_selector'
  | 'shape'
  | 'icon'
  | 'spacer';

export interface Variable {
  key: string;
  label: string;
  source: 'job' | 'customer' | 'organization' | 'custom';
  defaultValue?: string;
}

export interface SectionContentItem {
  id: string;
  order: number;
  type: 'field' | 'block';
  fieldKey?: string;
  block?: Block;
  alignment?: 'left' | 'center' | 'right';
  size?: 'small' | 'medium' | 'large';
  fieldStyle?: {
    backgroundColor?: string;
    textAlign?: 'left' | 'center' | 'right';
    fontSize?: 'small' | 'medium' | 'large';
  };
}

export interface PackageVisibilitySettings {
  showUnit: boolean;
  showQuantity: boolean;
  showUnitPrice: boolean;
  showLineItemTotal: boolean;
  showOnlyTotal: boolean;
  hiddenLineItemIds: string[];
  layoutStyle: 'table' | 'stacked';
}

export interface BlockSettings {
  align?: 'left' | 'center' | 'right';
  backgroundColor?: string;
  backgroundImage?: string;
  padding?: string;
  showUnitPrice?: boolean;
  showQuantity?: boolean;
  showTotal?: boolean;
  highlightTotal?: boolean;
  // Dynamic Image settings
  maxImages?: number;
  required?: boolean;
  captionStyle?: 'below' | 'overlay' | 'none';
  // Smart Field settings
  variableKey?: string;
  format?: 'text' | 'bold' | 'colored';
  defaultValue?: string;
  // Block size setting
  blockSize?: 'small' | 'medium' | 'large';
  // Video settings
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  videoWidth?: string;
  videoHeight?: string;
  // Button settings
  buttonText?: string;
  buttonUrl?: string;
  buttonColor?: string;
  buttonSize?: 'sm' | 'md' | 'lg';
  buttonIcon?: string;
  // Package Selector settings
  packageGroupIds?: string[];
  syncMode?: 'locked' | 'live';
  displayStyle?: 'good_better_best' | 'list' | 'comparison';
  visibilitySettings?: PackageVisibilitySettings;
  // Spacer settings
  height?: 'small' | 'medium' | 'large' | 'xlarge';
}

// 10 Most Popular Fonts
export const AVAILABLE_FONTS = [
  { name: 'Inter', label: 'Inter (Modern Sans)', family: 'Inter, sans-serif' },
  { name: 'Roboto', label: 'Roboto (Clean Sans)', family: 'Roboto, sans-serif' },
  { name: 'Open Sans', label: 'Open Sans (Friendly)', family: '"Open Sans", sans-serif' },
  { name: 'Lato', label: 'Lato (Professional)', family: 'Lato, sans-serif' },
  { name: 'Montserrat', label: 'Montserrat (Bold)', family: 'Montserrat, sans-serif' },
  { name: 'Playfair Display', label: 'Playfair (Elegant Serif)', family: '"Playfair Display", serif' },
  { name: 'Merriweather', label: 'Merriweather (Classic Serif)', family: 'Merriweather, serif' },
  { name: 'Raleway', label: 'Raleway (Stylish)', family: 'Raleway, sans-serif' },
  { name: 'Poppins', label: 'Poppins (Geometric)', family: 'Poppins, sans-serif' },
  { name: 'Nunito', label: 'Nunito (Rounded)', family: 'Nunito, sans-serif' },
];

export interface Block {
  id: string;
  type: BlockType;
  order?: number;
  
  // Free-form positioning (optional for section blocks)
  position?: {
    x: number;
    y: number;
    width: number;
    height: number | 'auto';
    zIndex: number;
  };
  
  // Advanced styling
  style?: {
    // Border
    borderWidth?: number;
    borderColor?: string;
    borderRadius?: number;
    borderStyle?: 'solid' | 'dashed' | 'dotted' | 'none';
    
    // Shadow
    boxShadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
    
    // Background
    backgroundColor?: string;
    opacity?: number;
    
    // Image-specific
    objectFit?: 'cover' | 'contain' | 'fill';
    clipPath?: string;
    
    // Typography
    fontFamily?: string;
    fontSize?: number;
    fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
    textColor?: string;
    lineHeight?: number;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    
    // Spacing
    padding?: number;
  };
  
  settings?: BlockSettings;
  content: any;
}

export interface Page {
  id: string;
  title: string;
  order: number;
  blocks: Block[];
  pageSettings?: {
    backgroundColor?: string;
    backgroundImage?: string;
    padding?: string;
  };
}

export interface GlobalStyles {
  primaryColor?: string;
  fontFamily?: string;
  fontSize?: string;
}

export interface TemplateLayout {
  pages: Page[];
  variables: Variable[];
  styles: GlobalStyles;
}

export interface ProposalTemplate {
  id: string;
  org_id: string;
  name: string;
  description?: string;
  doc_type: DocType;
  is_default: boolean;
  is_system_template: boolean;
  layout_json: TemplateLayout;
  variables_json?: Variable[];
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

export interface ProposalDocument {
  id: string;
  job_id?: string;
  org_id: string;
  template_id?: string;
  doc_type: DocType;
  status: ProposalStatus;
  title: string;
  layout_filled_json: TemplateLayout;
  public_share_id?: string;
  signed_at?: string;
  signed_by_name?: string;
  signed_by_email?: string;
  signed_ip?: string;
  signature_data?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CatalogItem {
  id: string;
  org_id: string;
  name: string;
  description?: string;
  category?: string;
  unit_price?: number;
  unit_type?: 'sqft' | 'lf' | 'each' | 'hour' | 'sq';
  material_cost?: number;
  labor_cost?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LineItem {
  id: string;
  name: string;
  description?: string;
  quantity: number;
  unit_type: string;
  unit_price: number;
  total: number;
}
